import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  updateDoc,
  getDocs,
  arrayUnion,
  setDoc,
} from "firebase/firestore";
import {
  Box,
  Avatar,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  CircularProgress,
  IconButton,
  Select,
  MenuItem,
  Chip,
  Skeleton,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
  Slide,
  Modal,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { showCustomToast } from "../misc/CustomToast";
import { LanguageContext } from "../context/LanguageContext";
import LeftMenu from "./menus/LeftMenu";
import HeaderBar from "./headers/HeaderBar";
import {
  getStorage,
  uploadBytes,
  ref as storageRef,
  getDownloadURL,
} from "firebase/storage";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import BusinessIcon from "@mui/icons-material/Business"; // pour “Société”
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import "react-awesome-button/dist/styles.css";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  getCurrentUserSubscriptions,
  getStripePayments,
} from "@invertase/firestore-stripe-payments";
import { getApp } from "firebase/app";
import Tooltip from "@mui/material/Tooltip";
import Switch from "@mui/material/Switch";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import PrivacyPolicy from "./ProvacyPolicy";

// ⚙️ une seule initialisation
const appStripe = getApp();
const paymentsStripe = getStripePayments(appStripe, {
  productsCollection: "products",
  customersCollection: "customers",
});

export default function ProfileDesktop() {
  const { translate } = useContext(LanguageContext);
  const { currentUser, logOut } = useAuth();
  const fileInputRef = useRef(null);
  const [role, setRole] = useState("Normal User");
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [editNameMode, setEditNameMode] = useState(false);
  const [newName, setNewName] = useState("");
  const [nameLoading, setNameLoading] = useState(false);
  const [roleLoadingIds, setRoleLoadingIds] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [openInviteModal, setOpenInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(true);
  const navigate = useNavigate();
  const ROLES = ["Normal User", "Admin", "Suspended"];
  const [inviteRole, setInviteRole] = useState("Normal User");
  const [inviteMessage, setInviteMessage] = useState("");
  const [adminEmails, setAdminEmails] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [openAddProjectModal, setOpenAddProjectModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [newPageListCategories, setNewPageListCategories] = useState([]);
  const [selectedNewProjectMembers, setSelectedNewProjectMembers] = useState(
    []
  );

  // ■ pour le modal d’édition
  const [openEditProjectModal, setOpenEditProjectModal] = useState(false);
  const [projectToEdit, setProjectToEdit] = useState(null);
  const [editedProjectMembers, setEditedProjectMembers] = useState([]);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactSubject, setContactSubject] = useState("");
  const [contactMessage, setContactMessage] = useState("");
  const [contactLoading, setContactLoading] = useState(false);
  const [honeypot, setHoneypot] = useState(""); // champ piège anti-bot
  const [contactEmail, setContactEmail] = useState(currentUser?.email || "");

  const [privacyOpen, setPrivacyOpen] = useState(false);
  const theme = useTheme();
  const fullScreenPrivacy = useMediaQuery(theme.breakpoints.down("sm"));

  // ⬇️ après: const functions = getFunctions();

  useEffect(() => {
    if (currentUser?.email) setContactEmail(currentUser.email);
  }, [currentUser]);

  const [darkMode, setDarkMode] = useState(() => {
    try {
      const saved = localStorage.getItem("darkMode");
      if (saved !== null) return JSON.parse(saved);
    } catch {}
    return false;
  });

  const [portalUrl, setPortalUrl] = useState(null);

  // 1) Ajoute cette constante (près de tes autres const en haut du composant)
  const MAX_FREE_COLLABS = 3;
  const FREE_PROJECT_LIMIT = 1;
  const FREE_TICKET_LIMIT = 50;

  // 2bis) Par celle-ci :

  // const handleOpenAddProjectModal = () => setOpenAddProjectModal(true);
  const handleOpenAddProjectModal = () => {
    if (!canCreateAnotherProject) {
      showCustomToast(translate("PremiumNeededForMoreProjects"), "error");
      return;
    }
    setOpenAddProjectModal(true);
  };
  const handleCloseAddProjectModal = () => {
    setOpenAddProjectModal(false);
    setNewProjectName("");
    setNewPageListCategories([]);
    setSelectedNewProjectMembers([]);
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const canCreateAnotherProject = isPremium || projects.length < 1;
  const inviteDisabled = !isPremium && companyUsers.length >= MAX_FREE_COLLABS;
  const projectLimit = isPremium ? "∞" : FREE_PROJECT_LIMIT;
  const memberLimit = isPremium ? "∞" : MAX_FREE_COLLABS;

  // 1️⃣ récupère si l’utilisateur est abonné
  useEffect(() => {
    if (!currentUser) return;
    getCurrentUserSubscriptions(paymentsStripe, { status: "active" })
      .then((subs) => setIsPremium(subs.length > 0))
      .catch(console.error);
  }, [currentUser]);

  const creationDate = useMemo(() => {
    if (!currentUser?.metadata?.creationTime) return null;
    return new Date(currentUser.metadata.creationTime);
  }, [currentUser]);

  const addProjectDisabled = !canCreateAnotherProject; // déjà calculé, on le réutilise pour le tooltip

  const handleCreateProject = async () => {
    if (!isPremium && projects.length >= 1) {
      showCustomToast(translate("PremiumNeededForMoreProjects"), "error");
      return;
    }

    // 🔹 Limite des membres si pas premium
    if (!isPremium && selectedNewProjectMembers.length >= 3) {
      showCustomToast(translate("Limit3CollaboratorsNonPremium"), "error");
      return;
    }
    if (!newProjectName.trim()) return;
    const id = newProjectName.trim();
    const refProj = doc(db, "projects", id);
    const snap = await getDoc(refProj);
    if (snap.exists()) {
      showCustomToast(translate("ProjectAlreadyExists"), "error");
      return;
    }
    const proj = {
      name: id,
      companyId: currentUserProfile.companyId,
      companyName: currentUserProfile.companyName,
      pageListCategories: newPageListCategories,
      createdAt: new Date(),
      memberIds: [
        currentUser.uid,
        ...selectedNewProjectMembers.map((u) => u.id),
      ],
      memberEmails: [
        currentUser.email,
        ...selectedNewProjectMembers.map((u) => u.email),
      ],
    };
    await setDoc(refProj, proj);
    showCustomToast(translate("ProjectCreated"), "success");
    handleCloseAddProjectModal();
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!isPremium) return; // on ne prépare que pour les premium
      try {
        const { data } = await createPortalLink({
          returnUrl: `${window.location.origin}/app/userprofile`,
        });
        if (mounted && data?.url) setPortalUrl(data.url);
      } catch (e) {
        console.error("createPortalLink error:", e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [isPremium]);

  const handleOpenBillingPortal = () => {
    if (portalUrl) window.location.assign(portalUrl);
  };

  useEffect(() => {
    if (!currentUserProfile?.companyId) return;

    // On récupère tous les profils admin de la boîte
    (async () => {
      const qAdmin = query(
        collection(db, "profilsUsers"),
        where("companyId", "==", currentUserProfile.companyId),
        where("role", "==", "Admin")
      );
      const snapAdmin = await getDocs(qAdmin);
      setAdminEmails(snapAdmin.docs.map((d) => d.data().email));
    })();
  }, [currentUserProfile]);

  useEffect(() => {
    if (!currentUserProfile?.companyId) return;
    const compRef = doc(db, "companies", currentUserProfile.companyId);
    const unsub = onSnapshot(compRef, (snap) => {
      const data = snap.data() || {};
      setPendingInvites(data.invitedEmails || []);
    });
    return unsub;
  }, [currentUserProfile]);

  const functions = getFunctions();
  const sendInviteEmail = httpsCallable(functions, "sendInviteEmail");
  const createPortalLink = httpsCallable(
    functions,
    "ext-firestore-stripe-payments-createPortalLink"
  );
  const sendContactMessage = httpsCallable(functions, "sendContactMessage");

  useEffect(() => {
    if (!currentUser) return;
    const profileRef = doc(db, "profilsUsers", currentUser.uid);
    // écoute continue
    const unsubscribe = onSnapshot(profileRef, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      setRole(data.role ?? "Normal User");
      setCurrentUserProfile({ id: snap.id, ...data });
    });
    return unsubscribe;
  }, [currentUser]);

  useEffect(() => {
    if (role !== "Admin" || !currentUserProfile?.companyId) return;
    setLoadingUsers(true);
    const q = query(
      collection(db, "profilsUsers"),
      where("companyId", "==", currentUserProfile.companyId)
    );
    return onSnapshot(q, (snap) => {
      const us = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((u) => u.id !== currentUser.uid);
      setCompanyUsers(us);
      setLoadingUsers(false);
    });
  }, [role, currentUserProfile, currentUser]);

  useEffect(() => {
    if (!currentUserProfile?.companyId) return;
    setProjectsLoading(true);
    const q = query(
      collection(db, "projects"),
      where("companyId", "==", currentUserProfile.companyId)
    );
    return onSnapshot(q, (snap) => {
      setProjects(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setProjectsLoading(false);
    });
  }, [currentUserProfile]);

  // ouvrir/fermer le dialog
  const handleOpenInvite = () => {
    setInviteEmail("");
    setOpenInviteModal(true);
  };
  const handleCloseInvite = () => setOpenInviteModal(false);

  // envoyer l’invitation par cloud function
  const handleSendInvite = async () => {
    if (!inviteEmail.match(/\S+@\S+\.\S+/)) {
      showCustomToast(translate("InvalidEmail"), "error");
      return;
    }
    setInviteLoading(true);
    // 0) Empêcher le doublon dans invitedEmails
    const compRef = doc(db, "companies", currentUserProfile.companyId);
    const compSnap = await getDoc(compRef);
    const alreadyInvited = compSnap
      .data()
      ?.invitedEmails?.includes(inviteEmail);
    if (alreadyInvited) {
      showCustomToast(translate("EmailAlreadyInvited"), "error");
      setInviteLoading(false);
      return;
    }

    // Vérifier qu’aucun profil n’existe déjà
    const qCheck = query(
      collection(db, "profilsUsers"),
      where("email", "==", inviteEmail)
    );
    const snap = await getDocs(qCheck);
    if (!snap.empty) {
      // si ce profil a déjà une boîte, on n’invite pas
      const profileData = snap.docs[0].data();
      if (profileData.companyId) {
        showCustomToast(translate("AlreadyMemberOfACompany"), "error");
        setInviteLoading(false);
        return;
      }
      showCustomToast(translate("EmailAlreadyExists"), "error");
      setInviteLoading(false);
      return;
    }

    try {
      await sendInviteEmail({
        email: inviteEmail,
        companyId: currentUserProfile.companyId,
        invitedBy: currentUser.email,
        role: inviteRole,
        message: inviteMessage,
      });
      const companyDoc = doc(db, "companies", currentUserProfile.companyId);
      await updateDoc(companyDoc, {
        invitedEmails: arrayUnion(inviteEmail),
      });
      showCustomToast(translate("InviteSent"), "success");
      handleCloseInvite();
    } catch {
      showCustomToast(translate("InviteFailed"), "error");
    } finally {
      setInviteLoading(false);
    }
  };

  useEffect(() => {
    if (!currentUser) return;
    const profileRef = doc(db, "profilsUsers", currentUser.uid);

    const unsubscribe = onSnapshot(profileRef, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      setRole(data.role ?? "Normal User");
      setCurrentUserProfile({ id: snap.id, ...data });

      // 🔹 On met à jour le Dark Mode depuis Firestore si dispo
      // if (data.darkMode !== undefined) {
      //   setDarkMode(data.darkMode);
      // }
      if (typeof data.darkMode === "boolean") {
        setDarkMode(data.darkMode); // pour refléter l’état du Switch
        try {
          const current = JSON.parse(
            localStorage.getItem("darkMode") ?? "null"
          );
          if (current !== data.darkMode) {
            localStorage.setItem("darkMode", JSON.stringify(data.darkMode));
            window.dispatchEvent(
              new CustomEvent("darkmode:changed", { detail: data.darkMode })
            );
          }
        } catch {}
      }
    });

    return unsubscribe;
  }, [currentUser]);

  const roleColors = {
    Admin: "#f44336", // rouge
    "Normal User": "#278ef4", // bleu
    Manager: "#ffa000", // orange (par ex.)
    Suspended: "#FFFFFF",
  };

  async function handlePhotoChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoLoading(true);
    try {
      // ➊ charger l'image dans un <img>
      const img = await new Promise((res, rej) => {
        const i = new Image();
        i.onload = () => res(i);
        i.onerror = rej;
        i.src = URL.createObjectURL(file);
      });
      // ➋ calculer le ratio pour 100px de largeur max
      const ratio = Math.min(1, 100 / img.width);
      const w = Math.round(img.width * ratio);
      const h = Math.round(img.height * ratio);
      // ➌ dessiner dans un canvas
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      // ➍ convertir en blob
      const blob = await new Promise((res) =>
        canvas.toBlob(res, file.type || "image/jpeg", 0.8)
      );
      // ➎ upload du blob
      const storage = getStorage();
      const path = `profilePhotos/${currentUser.uid}/${file.name}`;
      const imgRef = storageRef(storage, path);
      await uploadBytes(imgRef, blob);
      const url = await getDownloadURL(imgRef);
      await updateDoc(doc(db, "profilsUsers", currentUser.uid), {
        photoUrl: url,
      });
      setCurrentUserProfile((p) => ({ ...p, photoUrl: url }));
    } catch (err) {
      console.error(err);
      showCustomToast(translate("PhotoUploadFailed"), "error");
    } finally {
      setPhotoLoading(false);
    }
  }

  // changer le rôle d’un utilisateur
  const handleChangeRole = async (userId, newRole) => {
    try {
      const ref = doc(db, "profilsUsers", userId);
      await updateDoc(ref, { role: newRole });
      showCustomToast(translate("RoleUpdated"), "success");
    } catch (err) {
      console.error(err);
      showCustomToast(translate("RoleUpdateFailed"), "error");
    }
  };

  // const createPortalLink = httpsCallable(
  //   functions,
  //   "ext-firestore-stripe-payments-createPortalLink"
  // );

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        bgcolor: "background.default",
        color: "text.primary",
        minHeight: "100vh",
      }}
    >
      <LeftMenu />
      <Box sx={{ width: "90%", boxSizing: "border-box", ml: "5px" }}>
        <HeaderBar
          title={translate("UserProfile")}
          subtitle={translate("YourUserProfil")}
          showProjectSelect={false}
          // on passe le vrai name issu de Firestore, et on retombe sur email si jamais
          userName={currentUserProfile?.name || currentUser.email}
          userRole={role}
        />

        <Box
          sx={{
            width: "95%",
            height: "100vh",
            overflow: "hidden",
            pl: "20px",
            pr: 0,
            pt: "25px",
            bgcolor: "background.default",
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "40%",
              borderRadius: "20px",
              mb: "20px",
              border: 1,
              borderColor: "divider",
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              bgcolor: "background.paper",
              
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                p: 0,
              }}
            >
              <div
                style={{
                  width: "auto",
                  height: "100%",
                  paddingTop: "40px",
                  paddingLeft: "20px",
                }}
              >
                {/* input caché pour uploader la nouvelle photo */}
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handlePhotoChange}
                />

                <div
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    position: "relative",
                    width: 140,
                    height: 140,
                    cursor: "pointer",
                  }}
                >
                  {/* Avatar avec bordure et ombre */}
                  <Avatar
                    key={currentUserProfile?.photoUrl}
                    src={currentUserProfile?.photoUrl}
                    alt={currentUserProfile?.displayName || currentUser.email}
                    sx={{
                      width: 140,
                      height: 140,
                      // border: "4px solid white",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                    }}
                  />

                  {/* Overlay masqué par défaut et qui apparaît au hover */}
                  <Box
                    className="avatar-overlay"
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      bgcolor: "rgba(0,0,0,0.5)",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      opacity: 0,
                      transition: "opacity 0.3s",
                      "&:hover": {
                        opacity: 1,
                      },
                    }}
                  >
                    {photoLoading ? (
                      <CircularProgress color="inherit" size={24} />
                    ) : (
                      translate("EditPicture")
                    )}
                  </Box>
                </div>
              </div>

              {/* nom éditable */}
              <div
                style={{
                  display: "flex",
                  alignItems: "start",
                  gap: 8,
                  height: "100%",
                  paddingTop: "30px",
                  paddingLeft: "20px",
                  flexDirection: "column",
                }}
              >
                <div style={{ display: "flex", flexDirection: "row" }}>
                  {editNameMode ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <TextField
                        size="small"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                      />
                      <Button
                        size="small"
                        variant="contained"
                        disabled={nameLoading}
                        endIcon={nameLoading && <CircularProgress size={16} />}
                        onClick={async () => {
                          setNameLoading(true);
                          await updateDoc(
                            doc(db, "profilsUsers", currentUser.uid),
                            { name: newName }
                          );
                          setCurrentUserProfile((p) => ({
                            ...p,
                            name: newName,
                          }));
                          setNameLoading(false);
                          setEditNameMode(false);
                        }}
                      >
                        {translate("Save")}
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setEditNameMode(false)}
                      >
                        {translate("Cancel")}
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Typography
                        variant="h5"
                        component="div"
                        sx={{ fontWeight: 600 }}
                      >
                        {currentUserProfile?.name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setNewName(currentUserProfile?.name);
                          setEditNameMode(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </div>

                <Typography variant="body2" color="text.secondary">
                  {currentUserProfile?.email}
                </Typography>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    alignContent: "center",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "20px",
                  }}
                >
                  {isPremium && (
                    <Chip
                      label={translate("YouArePremium")}
                      size="small"
                      sx={{
                        mt: 1,
                        bgcolor: "gold",
                        color: "black",
                        fontWeight: 600,
                        boxShadow: "0 0 8px gold",
                        animation: "shimmer 2s infinite",
                        "@keyframes shimmer": {
                          "0%": { boxShadow: "0 0 5px gold" },
                          "50%": { boxShadow: "0 0 20px gold" },
                          "100%": { boxShadow: "0 0 5px gold" },
                        },
                      }}
                    />
                  )}
                  {isPremium && (
                    <Box
                      sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}
                    >
                      <Button
                        variant="outlined"
                        startIcon={<CreditCardIcon />}
                        onClick={handleOpenBillingPortal}
                        sx={{
                          borderRadius: "20px",
                          height: "30px",
                          fontSize: "10px",
                        }}
                      >
                        {translate("ManageSubscription") ||
                          "Manage subscription"}
                      </Button>
                    </Box>
                  )}
                </div>

                <Box
                  sx={{
                    width: "100%",
                    mt: 1,
                    p: 2,
                    borderRadius: 3,
                    background:
                      "linear-gradient(135deg, rgba(64,123,255,0.14), rgba(93,210,255,0.12))",
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow:
                      "0 10px 30px rgba(64,123,255,0.10), inset 0 1px 0 rgba(255,255,255,0.08)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <Chip
                    label={translate(role)}
                    size="medium"
                    sx={{
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      fontWeight: 700,
                      px: 1.5,
                      bgcolor: isPremium
                        ? "gold"
                        : role === "Admin"
                        ? "#d32f2f"
                        : "primary.main",
                      color: isPremium
                        ? "#1a1a1a"
                        : role === "Admin"
                        ? "#fff"
                        : "primary.contrastText",
                      boxShadow: isPremium
                        ? "0 0 12px rgba(255,215,0,0.6)"
                        : role === "Admin"
                        ? "0 6px 18px rgba(211,47,47,0.35)"
                        : "0 6px 18px rgba(64,123,255,0.28)",
                    }}
                  />
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{ fontWeight: 700, textAlign: "center" }}
                  >
                    {isPremium
                      ? "Premium - projets illimites, tickets illimites, collaborateurs illimites."
                      : `Plan gratuit - ${FREE_PROJECT_LIMIT} projet, ${FREE_TICKET_LIMIT} tickets/mois/projet, ${MAX_FREE_COLLABS} collaborateurs.`}
                  </Typography>
                  {!isPremium && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => navigate("/app/gotopremium")}
                      sx={{
                        mt: 1,
                        borderRadius: 999,
                        px: 2.5,
                        textTransform: "none",
                        fontWeight: 700,
                      }}
                    >
                      Passer Premium
                    </Button>
                  )}
                </Box>
                
              </div>
              {/* rôle en sous-titre */}
            </Box>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                gap: "20px",
                paddingRight: "40px",
                paddingTop: "20px",
                paddingBottom: "24px",
                
              }}
            >
              {currentUserProfile?.companyName && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    flexDirection: "column",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      gap: "5px",
                    }}
                  >
                    <BusinessIcon
                      sx={{ fontSize: 16, color: "text.secondary" }}
                      size="small"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {translate("Company")}
                    </Typography>
                  </div>

                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{ fontWeight: "500" }}
                  >
                    {currentUserProfile.companyName}
                  </Typography>
                </div>
              )}

              {creationDate && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    flexDirection: "column",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      gap: "5px",
                    }}
                  >
                    <CalendarTodayIcon
                      sx={{ fontSize: 16, color: "text.secondary" }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {translate("JoinedOn")}
                    </Typography>
                  </div>
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{ fontWeight: "500" }}
                  >
                    {creationDate.toLocaleDateString()}
                  </Typography>
                </div>
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={darkMode}
                    onChange={async (e) => {
                      const value = e.target.checked;
                      setDarkMode(value); // feedback instantané sur le Switch
                      try {
                        await updateDoc(
                          doc(db, "profilsUsers", currentUser.uid),
                          {
                            darkMode: value,
                          }
                        );
                        try {
                          localStorage.setItem(
                            "darkMode",
                            JSON.stringify(value)
                          );
                          window.dispatchEvent(
                            new CustomEvent("darkmode:changed", {
                              detail: value,
                            })
                          );
                        } catch {}
                      } catch (err) {
                        console.error("Erreur Dark Mode :", err);
                        showCustomToast(
                          translate("DarkModeSaveFailed"),
                          "error"
                        );
                      }
                    }}
                    color="primary"
                  />
                }
                label={
                  darkMode ? translate("DarkModeOn") : translate("DarkModeOff")
                }
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  gap: 1,
                  width: "100%",
                  flexWrap: "wrap",
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setContactOpen(true)}
                  sx={{ alignSelf: "flex-end" }}
                >
                  {translate("ContactUs") || "Contact us"}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setPrivacyOpen(true)}
                  sx={{ alignSelf: "flex-end" }}
                >
                  {translate("PrivacyPolicy") || "Politique de confidentialité"}
                </Button>
              </Box>
            </div>
          </Box>
          {currentUserProfile?.role === "Suspended" && (
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  display: "inline-block",
                  bgcolor: (t) =>
                    t.palette.mode === "dark"
                      ? "rgba(255,64,64,0.15)"
                      : "#ffe0e0",
                  color: (t) => (t.palette.mode === "dark" ? "#f99" : "#900"),
                  p: "5px",
                  mb: "8px",
                  borderRadius: "5px",
                  border: 1,
                  borderColor: (t) =>
                    t.palette.mode === "dark" ? "rgba(255,64,64,0.3)" : "#f88",
                  textAlign: "center",
                }}
              >
                <Typography variant="body1" fontWeight="bold">
                  {translate("YouAreSuspended")}
                </Typography>
                <Typography variant="body2">
                  {translate("ContactAdminsAt")} {adminEmails.join(", ")}
                </Typography>
              </Box>
            </div>
          )}

          {currentUserProfile?.role === "Suspended" ? (
            <div></div>
          ) : (
            <div
              style={{
                width: "100%",
                height: "45%",
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                gap: "20px",
              }}
            >
              <Box
                sx={{
                  width: "50%",
                  height: "100%",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: "20px",
                  bgcolor: "background.paper",
                }}
              >
                <div
                  style={{
                    padding: "0 0 20px",
                    // borderBottom: "1px solid lightgrey",
                    display: "flex",
                    flexDirection: "column",
                    height: "100%", // <<< prend toute la hauteur dispo
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flex: "0 0 15%",
                      borderTopRightRadius: "20px",
                      borderTopLeftRadius: "20px",
                      bgcolor: "background.paper",
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: "16px",
                        fontWeight: 500,
                        pl: "15px",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <span>{translate("TeamMembers")}</span>
                      <span
                        style={{
                          color:
                            companyUsers.length >= (isPremium ? Infinity : MAX_FREE_COLLABS)
                              ? "#d32f2f"
                              : "#2e7d32",
                          fontWeight: 700,
                        }}
                      >
                        ({companyUsers.length}/{memberLimit})
                      </span>
                      {!isPremium && (
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => navigate("/app/gotopremium")}
                          sx={{
                            borderRadius: 999,
                            textTransform: "none",
                            fontWeight: 700,
                            px: 1.5,
                            minHeight: "28px",
                          }}
                        >
                          Passer Premium
                        </Button>
                      )}
                    </Box>

                    <div style={{ paddingTop: "0px", paddingRight: "10px" }}>
                      {role === "Admin" && (
                        <Tooltip
                          // title={
                          //   inviteDisabled ? translate("GoPremiumToInvite") : ""
                          // }
                          title={
                            inviteDisabled
                              ? translate("Limit3CollaboratorsReachedInvite", {
                                  limit: MAX_FREE_COLLABS,
                                })
                              : ""
                          }
                          arrow
                          placement="top"
                        >
                          <span>
                            <Button
                              variant="contained"
                              startIcon={<AddCircleOutlineIcon />}
                              onClick={handleOpenInvite}
                              sx={{ borderRadius: "20px" }}
                              disabled={inviteDisabled}
                            >
                              {translate("InviteUser")}
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  </Box>

                  <Box
                    sx={{
                      flex: "1 1 85%",
                      minHeight: "85%",
                      pt: "10px",
                      px: "10px",
                      borderTop: 1,
                      borderColor: "divider",
                      overflowY: "auto",
                    }}
                  >
                    {companyUsers.length > 0 || pendingInvites.length > 0 ? (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px",
                        }}
                      >
                        {loadingUsers
                          ? [1, 2, 3].map((i) => (
                              <Skeleton
                                key={i}
                                variant="rectangular"
                                height={48}
                                sx={{
                                  borderRadius: "30px",
                                  marginBottom: "10px",
                                }}
                              />
                            ))
                          : companyUsers.map((u) => (
                              <div key={u.id} item xs={12} sm={6} md={4}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    border: 1,
                                    borderColor: "divider",
                                    borderRadius: "30px",
                                    px: "10px",
                                    py: "5px",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "row",
                                      gap: "10px",
                                      width: "30%",
                                    }}
                                  >
                                    <Avatar
                                      src={u?.photoUrl}
                                      alt={u.name || u.email}
                                      sx={{ width: 36, height: 36 }}
                                    />
                                    <div
                                      style={{
                                        fontSize: "14px",
                                        paddingTop: "0px",
                                        paddingBottom: "0px",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignContent: "center",
                                        alignItems: "center",
                                        fontWeight: "550",
                                      }}
                                    >
                                      {u.name}
                                    </div>
                                  </div>

                                  <div
                                    style={{
                                      fontSize: "14px",
                                      paddingTop: "5px",
                                      paddingBottom: "5px",
                                      width: "30%",
                                    }}
                                  >
                                    {u.displayName || u.email}
                                  </div>

                                  <div
                                    style={{
                                      width: "20%",
                                    }}
                                  >
                                    <Select
                                      size="small"
                                      value={u.role}
                                      disabled={roleLoadingIds.includes(u.id)}
                                      onChange={async (e) => {
                                        const newRole = e.target.value;
                                        setRoleLoadingIds((ids) => [
                                          ...ids,
                                          u.id,
                                        ]);
                                        try {
                                          await handleChangeRole(u.id, newRole);
                                        } finally {
                                          setRoleLoadingIds((ids) =>
                                            ids.filter((id) => id !== u.id)
                                          );
                                        }
                                      }}
                                      sx={{
                                        minWidth: 100,
                                        height: 32,
                                        backgroundColor:
                                          roleColors[u.role] || "grey",
                                        borderRadius: "16px",
                                        color:
                                          u.role === "Suspended"
                                            ? "black"
                                            : "white",
                                        "& .MuiSelect-icon": {
                                          color:
                                            u.role === "Suspended"
                                              ? "black"
                                              : "white",
                                        },
                                      }}
                                    >
                                      {ROLES.map((r) => (
                                        <MenuItem key={r} value={r}>
                                          {translate(r)}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </div>

                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      width: "20%",
                                      justifyContent: "flex-end",
                                    }}
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setUserToDelete(u);
                                        setConfirmOpen(true);
                                      }}
                                    >
                                      <DeleteIcon
                                        fontSize="small"
                                        color="error"
                                      />
                                    </IconButton>
                                  </Box>
                                </Box>
                              </div>
                            ))}

                        {pendingInvites
                          .filter(
                            (email) =>
                              !companyUsers.some((u) => u.email === email)
                          )
                          .map((email) => (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                gap: "10px",
                                border: "1px dashed",
                                borderColor: "divider",
                                borderRadius: "30px",
                                px: "10px",
                                py: "5px",
                                justifyContent: "space-between",
                              }}
                            >
                              <Avatar sx={{ width: 36, height: 36 }}>
                                {email[0].toUpperCase()}
                              </Avatar>
                              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                {email}
                              </Typography>
                              <Chip label={translate("Pending")} size="small" />
                            </Box>
                          ))}
                      </div>
                    ) : (
                      <Typography color="text.secondary">
                        {translate("NoColleagues")}
                      </Typography>
                    )}
                  </Box>
                </div>
              </Box>
              {role === "Admin" && (
                <Box
                  sx={{
                    width: "50%",
                    height: "100%",
                    borderRadius: "20px",
                    border: 1,
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <div
                    style={{
                      padding: "0 0 20px",
                      // borderBottom: "1px solid lightgrey",
                      display: "flex",
                      flexDirection: "column",
                      height: "100%", // <<< prend toute la hauteur dispo
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flex: "0 0 15%",
                        borderTopRightRadius: "20px",
                        borderTopLeftRadius: "20px",
                        bgcolor: "background.paper",
                      }}
                    >
                      <Box
                        sx={{ fontSize: "16px", fontWeight: 500, pl: "15px" }}
                      >
                        {translate("ProjectsList")}{" "}
                        <span
                          style={{
                            color:
                              projects.length >= (isPremium ? Infinity : FREE_PROJECT_LIMIT)
                                ? "#d32f2f"
                                : "#2e7d32",
                            fontWeight: 700,
                          }}
                        >
                          ({projects.length}/{projectLimit})
                        </span>
                        {!isPremium && (
                          <Button
                            variant="text"
                            size="small"
                            onClick={handleOpenAddProjectModal}
                            sx={{
                              ml: 1,
                              borderRadius: 999,
                              textTransform: "none",
                              fontWeight: 700,
                              px: 1.5,
                              minHeight: "28px",
                            }}
                          >
                            Passer Premium
                          </Button>
                        )}
                      </Box>

                      <div style={{ paddingTop: "0px", paddingRight: "10px" }}>
                        <Tooltip
                          title={
                            addProjectDisabled
                              ? translate("PremiumNeededForMoreProjects")
                              : ""
                          }
                          arrow
                          placement="top"
                        >
                          <span>
                            <Button
                              variant="contained"
                              startIcon={<AddCircleOutlineIcon />}
                              sx={{ borderRadius: "20px" }}
                              onClick={handleOpenAddProjectModal}
                              disabled={addProjectDisabled}
                            >
                              {translate("AddAProject")}
                            </Button>
                          </span>
                        </Tooltip>
                      </div>
                    </Box>

                    <Box
                      sx={{
                        flex: "1 1 85%",
                        minHeight: "85%",
                        pt: "10px",
                        px: "10px",
                        borderTop: 1,
                        borderColor: "divider",
                        overflowY: "auto",
                      }}
                    >
                      {projectsLoading ? (
                        [1, 2, 3].map((i) => (
                          <Skeleton
                            key={i}
                            variant="rectangular"
                            height={48}
                            sx={{ borderRadius: "30px", mb: 1 }}
                          />
                        ))
                      ) : projects.length > 0 ? (
                        projects.map((proj) => (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              border: 1,
                              borderColor: "divider",
                              borderRadius: "30px",
                              px: "10px",
                              py: "5px",
                              mb: 1,
                            }}
                          >
                            <Typography sx={{ fontWeight: 500 }}>
                              {proj.name}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setProjectToEdit(proj);
                                // pré‐sélectionne les membres existants
                                setEditedProjectMembers(
                                  companyUsers.filter((u) =>
                                    (proj.memberIds || []).includes(u.id)
                                  )
                                );
                                setOpenEditProjectModal(true);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))
                      ) : (
                        <Typography color="text.secondary">
                          {translate("NoProjects")}
                        </Typography>
                      )}
                    </Box>
                  </div>
                </Box>
              )}
            </div>
          )}
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Box sx={{ textAlign: "center", mt: 4 }}>
              <Button
                variant="contained"
                color="error"
                onClick={async () => {
                  await logOut();
                  navigate("/login");
                }}
              >
                {translate("Signout")}
              </Button>
            </Box>
          </div>
        </Box>
      </Box>

      <Modal
        open={openEditProjectModal}
        onClose={() => setOpenEditProjectModal(false)}
        closeAfterTransition
      >
        <Slide direction="left" in={openEditProjectModal}>
          <Box
            sx={{
              position: "fixed",
              top: "5%",
              right: 0,
              width: "40%",
              height: "90%",
              bgcolor: "background.paper",
              boxShadow: 24,
              borderRadius: "15px",
              p: 2,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
            >
              <Typography variant="h6">
                {translate("EditProject")} – {projectToEdit?.name}
              </Typography>
              <IconButton onClick={() => setOpenEditProjectModal(false)}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Autocomplete membres à éditer */}
            <Autocomplete
              multiple
              options={companyUsers}
              disableCloseOnSelect
              getOptionLabel={(u) =>
                u.name ? `${u.name} (${u.email})` : u.email
              }
              value={editedProjectMembers}
              onChange={(_, v) => setEditedProjectMembers(v)}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Avatar
                    src={option.photoUrl}
                    sx={{ width: 24, height: 24, mr: 1 }}
                  />
                  {option.name
                    ? `${option.name} (${option.email})`
                    : option.email}
                </li>
              )}
              renderTags={(tags, getTagProps) =>
                tags.map((option, idx) => (
                  <Chip
                    {...getTagProps({ index: idx })}
                    key={option.id}
                    avatar={
                      <Avatar
                        src={option.photoUrl}
                        sx={{ width: 20, height: 20 }}
                      />
                    }
                    label={
                      option.name
                        ? `${option.name} (${option.email})`
                        : option.email
                    }
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={translate("ProjectMembers")}
                  placeholder={translate("SelectMembers")}
                  fullWidth
                  margin="dense"
                />
              )}
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <Button onClick={() => setOpenEditProjectModal(false)}>
                {translate("Cancel")}
              </Button>
              <Button
                variant="contained"
                onClick={async () => {
                  // 🔹 Bloque si non premium et +3 collaborateurs
                  if (!isPremium && editedProjectMembers.length > 3) {
                    showCustomToast(
                      translate("Limit3CollaboratorsNonPremium"),
                      "error"
                    );
                    return;
                  }
                  const ref = doc(db, "projects", projectToEdit.id);
                  await updateDoc(ref, {
                    memberIds: editedProjectMembers.map((u) => u.id),
                    memberEmails: editedProjectMembers.map((u) => u.email),
                  });
                  showCustomToast(translate("ProjectUpdated"), "success");
                  setOpenEditProjectModal(false);
                }}
              >
                {translate("Save")}
              </Button>
            </Box>
          </Box>
        </Slide>
      </Modal>

      <Modal
        open={openAddProjectModal}
        onClose={handleCloseAddProjectModal}
        closeAfterTransition
      >
        <Slide direction="left" in={openAddProjectModal}>
          <Box
            sx={{
              position: "fixed",
              top: "5%",
              right: 0,
              width: "40%",
              height: "90%",
              bgcolor: "background.paper",
              boxShadow: 24,
              borderRadius: "15px",
              p: 2,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <div>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Typography variant="h6">{translate("AddProject")}</Typography>
                <IconButton onClick={handleCloseAddProjectModal}>
                  <CloseIcon />
                </IconButton>
              </Box>
              <TextField
                label={translate("ProjectName")}
                fullWidth
                margin="dense"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
              />

              <Autocomplete
                multiple
                options={companyUsers.filter((u) => u.id !== currentUser.uid)}
                getOptionLabel={(u) =>
                  u.name ? `${u.name} (${u.email})` : u.email
                }
                value={selectedNewProjectMembers}
                onChange={(_, v) => setSelectedNewProjectMembers(v)}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Avatar
                      src={option.photoUrl}
                      sx={{ width: 24, height: 24, mr: 1 }}
                    />
                    {option.name
                      ? `${option.name} (${option.email})`
                      : option.email}
                  </li>
                )}
                renderTags={(tags, getTagProps) =>
                  tags.map((option, idx) => (
                    <Chip
                      {...getTagProps({ index: idx })}
                      key={option.id}
                      avatar={
                        <Avatar
                          src={option.photoUrl}
                          sx={{ width: 20, height: 20 }}
                        />
                      }
                      label={
                        option.name
                          ? `${option.name} (${option.email})`
                          : option.email
                      }
                      size="small"
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={translate("ProjectMembers")}
                    placeholder={translate("SelectMembers")}
                    margin="dense"
                    fullWidth
                  />
                )}
                sx={{ mb: 2 }}
              />
            </div>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <Button onClick={handleCloseAddProjectModal}>
                {translate("Cancel")}
              </Button>

              <Tooltip
                title={
                  !canCreateAnotherProject
                    ? translate("PremiumNeededForMoreProjects")
                    : ""
                }
                arrow
                placement="top"
              >
                <span>
                  <Button
                    variant="contained"
                    onClick={handleCreateProject}
                    disabled={!canCreateAnotherProject}
                  >
                    {translate("Create")}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Slide>
      </Modal>
      <Dialog
        open={privacyOpen}
        onClose={() => setPrivacyOpen(false)}
        fullScreen={fullScreenPrivacy}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {translate("PrivacyPolicyTitle") || "Privacy Policy"}
        </DialogTitle>
        <DialogContent dividers>
          <PrivacyPolicy />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrivacyOpen(false)}>
            {translate("Close") || "Close"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ——— Dialog de confirmation de suppression ——— */}
      <Dialog open={contactOpen} onClose={() => setContactOpen(false)}>
        <DialogTitle>{translate("ContactUs") || "Contact us"}</DialogTitle>
        <DialogContent>
          <TextField
            label={translate("EmailAddress") || "Email"}
            type="email"
            fullWidth
            margin="dense"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
          />
          <TextField
            label={translate("Subject") || "Subject"}
            fullWidth
            margin="dense"
            value={contactSubject}
            onChange={(e) => setContactSubject(e.target.value)}
          />
          <TextField
            label={translate("Message") || "Message"}
            fullWidth
            multiline
            rows={4}
            margin="dense"
            value={contactMessage}
            onChange={(e) => setContactMessage(e.target.value)}
          />
          {/* Champ honeypot invisible (anti-bot) */}
          <input
            type="text"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            tabIndex={-1}
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "-5000px",
              height: 0,
              width: 0,
              opacity: 0,
              pointerEvents: "none",
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactOpen(false)}>
            {translate("Cancel") || "Cancel"}
          </Button>
          <Button
            variant="contained"
            disabled={contactLoading || !contactMessage.trim()}
            onClick={async () => {
              setContactLoading(true);
              try {
                await sendContactMessage({
                  subject: contactSubject,
                  message: contactMessage,
                  fromEmail: contactEmail,
                  honeypot,
                });
                showCustomToast(
                  translate("MessageSent") || "Message sent",
                  "success"
                );
                setContactOpen(false);
                setContactSubject("");
                setContactMessage("");
                setContactEmail(currentUser?.email || "");
                setHoneypot("");
              } catch (e) {
                showCustomToast(
                  translate("MessageFailed") || "Failed to send message",
                  "error"
                );
              } finally {
                setContactLoading(false);
              }
            }}
          >
            {contactLoading ? (
              <CircularProgress size={18} />
            ) : (
              translate("Send") || "Send"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>
          {translate("ConfirmDeleteUser", {
            name: userToDelete?.name || userToDelete?.email,
          })}
        </DialogTitle>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>
            {translate("Cancel")}
          </Button>
          <Button
            color="error"
            onClick={async () => {
              setConfirmOpen(false);
              if (!userToDelete) return;
              try {
                // On retire simplement son companyId
                await updateDoc(doc(db, "profilsUsers", userToDelete.id), {
                  companyId: null,
                });
                showCustomToast(
                  translate("UserDeletedSuccessfully", {
                    name: userToDelete.name || userToDelete.email,
                  }),
                  "success"
                );
                // rafraîchir localement la liste
                setCompanyUsers((list) =>
                  list.filter((x) => x.id !== userToDelete.id)
                );
              } catch (err) {
                console.error(err);
                showCustomToast(translate("UserDeleteFailed"), "error");
              }
            }}
          >
            {translate("Delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openInviteModal} onClose={handleCloseInvite}>
        <DialogTitle>{translate("InviteUser")}</DialogTitle>
        <DialogContent>
          <div style={{ fontSize: "12px", paddingLeft: "5px" }}>
            {translate("InviteByEmail")}
          </div>
          <TextField
            autoFocus
            margin="dense"
            label={translate("EmailAddress")}
            type="email"
            fullWidth
            variant="outlined"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            sx={{
              mb: 0, // supprime toute marge basse
              mt: 0.5, // si tu veux conserver un tout petit espace au-dessus
            }}
          />

          <FormControl component="fieldset" sx={{ mt: 0 }}>
            <RadioGroup
              row
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
            >
              {ROLES.map((r) => (
                <FormControlLabel
                  key={r}
                  value={r}
                  control={<Radio size="small" />}
                  label={translate(r)}
                  sx={{
                    // on cible directement le label interne
                    "& .MuiFormControlLabel-label": {
                      fontSize: "12px", // <- ta nouvelle taille
                    },
                  }}
                />
              ))}
            </RadioGroup>
          </FormControl>

          <div
            style={{
              fontSize: "12px",
              paddingLeft: "5px",
              marginTop: "10px",
            }}
          >
            {translate("SendAMessage")}
          </div>
          <TextField
            label={translate("OptionalMessage")}
            multiline
            rows={3}
            fullWidth
            sx={{ mt: 0, paddingTop: "5px" }}
            value={inviteMessage}
            onChange={(e) => setInviteMessage(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ paddingRight: "25px" }}>
          <Button onClick={handleCloseInvite}>{translate("Cancel")}</Button>
          <Button
            onClick={handleSendInvite}
            variant="contained"
            disabled={inviteLoading}
            endIcon={inviteLoading && <CircularProgress size={16} />}
          >
            {translate("SendInvite")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
